import {
  CheckBox,
  Save,
  CheckCircleOutline,
  Assessment,
  Download,
  Add,
  PublishedWithChanges,
  Print
} from '@mui/icons-material';
import {
  Box,
  ButtonGroup,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  useMediaQuery
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router-dom';
import { FC, useEffect, useState, useMemo, useContext } from 'react';
import {
  Button as SMButton,
  Loader,
  PageTitleWrapper,
  PageTitleWithButton,
  Button,
  InlineItemValue
} from '../../../components';
import {
  completeChecklist,
  getPlaceOrderChecklistDetails,
  getPlaceOrderChecklistDetailsPreview,
  updateChecklist,
  getDeficiencyReport,
  checklistRequestRecheck,
  createDeficiencyReport,
  mergePunchlistPurchaseOrderChecklist,
  processPurchaseOrderChecklist,
  getNeighborhoodVendors
} from '../../../fetch';
import {
  IMaterialsDelivered,
  IMaterialsProvided,
  IPlaceOrderChecklistDetails,
  IWorkItem
} from '../../../models';
import {
  formatDateWithTimeAndSeconds,
  downloadFile,
  formatDate,
  formatMoney
} from '../../../helpers';
import { MaterialsDelivered } from './MaterialsDelivered';
import { WorkItems } from './WorkItems';
import { MaterialsProvided } from './MaterialsProvided';
import { UserContext } from '../../../context';
import { useFlags } from '../../../hooks';

interface IChecklistDetailsProps {
  queryId: string;
  isChecklistReview?: boolean;
  isModalPreview?: boolean;
}

export const ChecklistDetails: FC<IChecklistDetailsProps> = ({
  queryId,
  isChecklistReview,
  isModalPreview
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { purchaseOrderId }: { purchaseOrderId: string } = useParams();
  const history = useHistory();
  const queryString = window.location.search;
  const { isBillingAdmin, isFinancialAdmin, isAdmin } = useContext(UserContext);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklist, setChecklist] = useState<IPlaceOrderChecklistDetails>(null);
  const [notes, setNotes] = useState(null);
  const [cannotVerify, setCannotVerify] = useState(false);
  const [materialsDelivered, setMaterialsDelivered] = useState<IMaterialsDelivered[]>(null);
  const [materialsProvided, setMaterialsProvided] = useState<IMaterialsProvided[]>(null);
  const [workItems, setWorkItems] = useState<IWorkItem[]>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isGenerating, setGenerating] = useState<boolean>(false);
  const [isRequesting, setRequesting] = useState<boolean>(false);
  const [pdfReport, setPdfReport] = useState<any>(null);
  const [isAddingToPunchlist, setIsAddingToPunchlist] = useState<boolean>(false);
  const [isProcessingPO, setIsProcessingPO] = useState<boolean>(false);
  const [hasReturnVendor, setHasReturnVendor] = useState<boolean>(false);
  const { createReturnPoButton: createReturnPoButtonFF } = useFlags();

  const canProcessPO = isAdmin || isBillingAdmin || isFinancialAdmin;

  // check here to make sure the attached sageVendorId is listed as a return vendor in this API call
  const checkForHasReturnVendor = async (
    neighborhoodId: string | number,
    lotId: string | number,
    sageVendorId: number
  ): Promise<boolean> => {
    try {
      const res = await getNeighborhoodVendors(neighborhoodId, {
        lotNumber: lotId,
        isReturnPO: true
      });
      const selected = res.records.find(vendor => vendor.id === sageVendorId);
      if (selected) {
        return true;
      }
      return false;
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChecklistDetails = async (showLoader?: boolean) => {
    if (showLoader) {
      setChecklistLoading(true);
    }
    try {
      if (isModalPreview) {
        const res = await getPlaceOrderChecklistDetailsPreview(
          parseInt(queryId),
          isChecklistReview
        );
        setChecklist(res);
        setNotes(res.notes ?? '');
        setCannotVerify(res.cannotVerify);
        setMaterialsDelivered(res.materialsDelivered);
        setWorkItems(res.workItems);
        if (res.materialsDelivered.length > 0) {
          const hasReturnVendor = await checkForHasReturnVendor(
            res.neighborhoodId,
            res.lotNumber,
            res.sageVendorId
          );
          setHasReturnVendor(hasReturnVendor);
        }
      } else {
        const res = await getPlaceOrderChecklistDetails(parseInt(queryId), isChecklistReview);
        setChecklist(res);
        setNotes(res.notes ?? '');
        setCannotVerify(res.cannotVerify);
        setMaterialsDelivered(res.materialsDelivered);
        setMaterialsProvided(res.materialsProvided);
        setWorkItems(res.workItems);
        if (res.materialsDelivered.length > 0) {
          const hasReturnVendor = await checkForHasReturnVendor(
            res.neighborhoodId,
            res.lotNumber,
            res.sageVendorId
          );
          setHasReturnVendor(hasReturnVendor);
        }
      }
    } catch (error: any) {
      enqueueSnackbar(`Redirecting...`, {
        variant: 'info'
      });
      enqueueSnackbar(error?.Detail ?? `Error loading checklist details.`, {
        variant: 'error'
      });
      return setTimeout(function () {
        history.push(
          isChecklistReview
            ? `/production/checklists/checklist-review`
            : '/production/checklists/manage-checklists'
        );
      }, 3000);
    } finally {
      setChecklistLoading(false);
    }
  };

  const hasCreatedPunchlist = useMemo(() => checklist?.hasAllItemsOnPunchLists, [checklist]);

  const handleMergePunchlist = async () => {
    try {
      await mergePunchlistPurchaseOrderChecklist(checklist.placeOrderChecklistId);
    } catch (error) {
      return enqueueSnackbar(error?.Detail, {
        variant: 'error'
      });
    }
  };

  const handleAddToPunchlist = async () => {
    try {
      setIsAddingToPunchlist(true);
      const data = {
        cannotVerify: cannotVerify,
        notes: notes ?? '',
        workItems: workItems.map(item => ({
          workItemId: item.workItemId,
          isSuccessful: item.meetsStandards,
          note: item.note
        })),
        materialsProvided: [],
        materialsDelivered: materialsDelivered.map(md => {
          return {
            materialsDeliveredId: md.materialsDeliveredId,
            receivedQuantity: md.quantityReceived ?? 0
          };
        })
      };
      // save before merging punchlist
      await updateChecklist(parseInt(queryId), data);
      await handleMergePunchlist();
      enqueueSnackbar('Success, punchlist updated!', {
        variant: 'success'
      });
      fetchChecklistDetails();
    } catch (error) {
      enqueueSnackbar(error?.Detail || 'Error updating punchlist, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsAddingToPunchlist(false);
    }
  };

  const handleSaving = async () => {
    setIsSaving(true);
    try {
      await updateChecklist(parseInt(queryId), {
        cannotVerify: cannotVerify,
        notes: notes ?? '',
        workItems: workItems.map(item => ({
          workItemId: item.workItemId,
          isSuccessful: item.meetsStandards,
          note: item.note
        })),
        materialsProvided: [],
        materialsDelivered: materialsDelivered.map(md => {
          return {
            materialsDeliveredId: md.materialsDeliveredId,
            receivedQuantity: md.quantityReceived ?? 0
          };
        })
      });
      enqueueSnackbar(`Checklist saved!`, {
        variant: 'success'
      });
      setIsDirty(false);
      fetchChecklistDetails(false);
    } catch (error) {
      enqueueSnackbar(error?.Detail || 'Error updating punchlist, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleCompleting = async () => {
    setIsCompleting(true);
    try {
      const data = {
        cannotVerify: cannotVerify,
        notes: notes ?? '',
        workItems: workItems.map(item => ({
          workItemId: item.workItemId,
          isSuccessful: item.meetsStandards,
          note: item.note
        })),
        materialsProvided: [],
        materialsDelivered: materialsDelivered.map(md => {
          return {
            materialsDeliveredId: md.materialsDeliveredId,
            receivedQuantity: md.quantityReceived ?? 0
          };
        })
      };
      // save before completting
      await updateChecklist(parseInt(queryId), data);
      await completeChecklist(parseInt(queryId), data);
      enqueueSnackbar(`Checklist completed!`, {
        variant: 'success'
      });
      fetchChecklistDetails(false);
    } catch (error) {
      enqueueSnackbar(error?.Detail || 'Error updating punchlist, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsCompleting(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const createRes = await createDeficiencyReport(queryId);
      const res = await getDeficiencyReport(queryId);

      if (createRes?.Detail || createRes.Status >= 400) {
        enqueueSnackbar(
          createRes?.Detail ?? 'Error, generating deficiency report, please try again.',
          {
            variant: 'error'
          }
        );
      } else {
        enqueueSnackbar(`Deficiency Report has been emailed!`, {
          variant: 'success'
        });
      }
      setPdfReport(res);
    } catch (error) {
      enqueueSnackbar(error?.Detail ?? 'Error, generating deficiency report, please try again.', {
        variant: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleRequestRecheck = async () => {
    setRequesting(true);
    try {
      const res = await checklistRequestRecheck(queryId);
      if (res?.Detail || res.Status >= 400) {
        return enqueueSnackbar(res?.Detail ?? 'Error, requesting a recheck on this checklist.', {
          variant: 'error'
        });
      }
      enqueueSnackbar(`Recheck Requested Successfully!`, {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar('Error, requesting a recheck on this checklist.', {
        variant: 'error'
      });
    } finally {
      setRequesting(false);
    }
  };

  const handleProcessingPO = async () => {
    setIsProcessingPO(true);
    try {
      if (isChecklistReview) {
        await processPurchaseOrderChecklist({
          purchaseOrderCheckListIds: [checklist.placeOrderChecklistId]
        });
        enqueueSnackbar('Checklist successfully processed!', {
          variant: 'success'
        });
      }
      return;
    } catch (error) {
      enqueueSnackbar(
        error?.Detail ?? `Error processing checklist. Please refresh and try again.`,
        {
          variant: 'error'
        }
      );
    } finally {
      setIsProcessingPO(false);
      fetchChecklistDetails(true);
    }
  };

  useEffect(() => {
    fetchChecklistDetails(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (checklist) {
      if (
        (checklist?.notes ?? '') !== notes ||
        checklist?.cannotVerify !== cannotVerify ||
        !deepEqual(checklist.materialsDelivered, materialsDelivered) ||
        !deepEqual(checklist.workItems, workItems)
      ) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, cannotVerify, materialsDelivered, workItems]);

  const hasMaterialsDelivered = useMemo(() => materialsDelivered?.length > 0, [materialsDelivered]);
  const hasMaterialsProvided = useMemo(() => materialsProvided?.length > 0, [materialsProvided]);
  const hasWorkItems = useMemo(() => workItems?.length > 0, [workItems]);

  const isChecklistDisabled = useMemo(
    () =>
      isCompleting ||
      // check the user has completed all materialsDelivered
      (hasMaterialsDelivered &&
        materialsDelivered.filter(d => !!d.quantityReceived).length !==
          materialsDelivered?.length) ||
      // check the user has completed all work items
      (hasWorkItems &&
        workItems.filter(d => d.meetsStandards !== null).length !== workItems?.length),
    [materialsDelivered, isCompleting, hasMaterialsDelivered, workItems, hasWorkItems]
  );

  return (
    <>
      <Box mt={isModalPreview ? 1 : 0}>
        <PageTitleWrapper>
          <PageTitleWithButton
            pageTitleBackProps={
              isModalPreview
                ? undefined
                : isChecklistReview
                ? {
                    backText: 'Back to Checklist Review',
                    backUrl: `/production/checklists/checklist-review${queryString}`,
                    title: `Checklist Review #${queryId ?? purchaseOrderId}`
                  }
                : {
                    backText: 'Back to Manage Checklists',
                    backUrl: `/production/checklists/manage-checklists${queryString}`,
                    title: `Checklist Details #${queryId ?? purchaseOrderId}`
                  }
            }
            pageTitleProps={
              isModalPreview
                ? { title: `Checklist Details #${queryId ?? purchaseOrderId}` }
                : undefined
            }
            button={
              isModalPreview ? (
                <Button
                  id="print-checklist"
                  variant="contained"
                  color="primary"
                  startIcon={<Print />}
                  onClick={() => window.print()}
                  fullWidth={isMobile}
                  disabled={checklistLoading}
                >
                  Print Checklist
                </Button>
              ) : (
                <>
                  {!checklistLoading && (
                    <LoadingButton
                      variant="contained"
                      disabled={isAddingToPunchlist || (hasCreatedPunchlist && !isDirty)}
                      startIcon={<Add />}
                      loadingPosition="start"
                      loading={isAddingToPunchlist}
                      onClick={handleAddToPunchlist}
                    >
                      {hasCreatedPunchlist
                        ? 'Save and Update Punchlist'
                        : 'Save and Add to Punchlist'}
                    </LoadingButton>
                  )}
                </>
              )
            }
          />
        </PageTitleWrapper>
      </Box>
      {(isSaving || isCompleting) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      {checklistLoading && (
        <Loader position="centered" type="inline">
          Loading...
        </Loader>
      )}
      {checklist && !checklistLoading && (
        <Box mt={-0.5}>
          {checklist?.materialsDelivered?.length > 0 &&
            hasReturnVendor &&
            createReturnPoButtonFF &&
            !isModalPreview && (
              <Box
                display="flex"
                mb={isMobile ? 1 : 0}
                justifyContent={isMobile ? 'flex-start' : 'flex-end'}
              >
                <Button
                  id="create-return-po"
                  variant="contained"
                  color="primary"
                  asLink
                  to={`/production/orders/place-order?purchaseOrderId=${checklist.purchaseOrderId}`}
                >
                  Create Return PO
                </Button>
              </Box>
            )}
          <Grid container spacing={isMobile ? 0.25 : 0.5}>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Purchase Order ID" value={checklist.purchaseOrderId} />
            </Grid>
            <Grid item xs={12} md={6} lg={8}>
              <InlineItemValue
                label="Total Price"
                value={checklist.totalPrice ? formatMoney(checklist.totalPrice) : ''}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Neighborhood" value={checklist.neighborhoodName} />
            </Grid>
            <Grid item xs={12} md={6} lg={8}>
              <InlineItemValue
                label="Address"
                value={`${checklist.address || ''} ${checklist.city || ''}${
                  checklist.state ? `, ${checklist.state}` : ''
                } ${checklist.zip || ''}`}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Phase" value={checklist.phaseName} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Job" value={checklist.jobId} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Lot" value={checklist.lotNumber} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue
                label="Completed On"
                value={checklist.completedOn ? formatDate(checklist.completedOn) : checklist.status}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Completed By" value={checklist.completedBy} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Vendor" value={checklist.vendor} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue label="Task" value={checklist.sageTaskId} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InlineItemValue
                label="Able to Verify"
                value={
                  (!cannotVerify).toString().charAt(0).toUpperCase() +
                  (!cannotVerify).toString().slice(1)
                }
              />
            </Grid>
            {isChecklistReview && (
              <>
                <Grid item xs={12} md={6} lg={4}>
                  <InlineItemValue
                    label="Checklist Started"
                    value={formatDateWithTimeAndSeconds(checklist?.startedOn)}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <InlineItemValue
                    label="Checklist Completed"
                    value={formatDateWithTimeAndSeconds(checklist?.completedOn)}
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <InlineItemValue label="Duration" value={checklist.duration} />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <InlineItemValue label="Notes" value={checklist.notes} />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {hasWorkItems && (
              <Grid item xs={12}>
                <WorkItems
                  isDisabled={isChecklistReview || isModalPreview}
                  setWorkItems={setWorkItems}
                  workItems={workItems}
                  checklistLoading={checklistLoading}
                />
              </Grid>
            )}
            {hasMaterialsProvided && (
              <Grid item xs={12}>
                <MaterialsProvided
                  materialsProvided={materialsProvided}
                  checklistLoading={checklistLoading}
                />
              </Grid>
            )}
            {hasMaterialsDelivered && (
              <Grid item xs={12}>
                <MaterialsDelivered
                  isDisabled={isChecklistReview || isModalPreview}
                  setMaterialsDelivered={setMaterialsDelivered}
                  materialsDelivered={materialsDelivered}
                  checklistLoading={checklistLoading}
                />
              </Grid>
            )}
            {!isChecklistReview && !isModalPreview && (
              <>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        color="primary"
                        checked={cannotVerify}
                        id="active-inactive"
                        onChange={() => {
                          setCannotVerify(!cannotVerify);
                        }}
                      />
                    }
                    label="Cannot Verify"
                  />
                </Grid>
                <Grid item xs={12} sx={{ marginTop: isMobile ? '1rem' : 0 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Notes"
                    name="notes"
                    multiline
                    minRows={3}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Box
                display="flex"
                flexDirection={{
                  xs: 'column',
                  md: 'row'
                }}
                justifyContent={{
                  xs: 'center',
                  md: 'flex-start'
                }}
              >
                <Box marginBottom={{ xs: '1rem', md: 0 }}>
                  <Box
                    display="flex"
                    sx={{
                      flexDirection: {
                        xs: 'column',
                        md: 'row'
                      },
                      width: {
                        xs: '100%',
                        md: 'auto'
                      }
                    }}
                  >
                    {isChecklistReview && checklist?.status !== 'Meets Standards' && (
                      <>
                        {checklist?.deficientItems && (
                          <>
                            <LoadingButton
                              sx={{
                                marginBottom: {
                                  xs: '1rem',
                                  md: 0
                                },
                                marginRight: {
                                  xs: 0,
                                  md: '1rem'
                                }
                              }}
                              color="primary"
                              variant="contained"
                              loading={isGenerating}
                              disabled={isGenerating}
                              loadingPosition="start"
                              startIcon={<Assessment />}
                              onClick={handleGenerateReport}
                            >
                              Generate Report
                            </LoadingButton>
                            {!!pdfReport && (
                              <SMButton
                                sx={{
                                  marginBottom: {
                                    xs: '1rem',
                                    md: 0
                                  },
                                  marginRight: {
                                    xs: 0,
                                    md: '1rem'
                                  }
                                }}
                                id="save"
                                color="secondary"
                                variant="contained"
                                startIcon={<Download />}
                                onClick={() => {
                                  downloadFile(
                                    pdfReport,
                                    `purchase-order-deficiency-report-${queryId}`
                                  );
                                }}
                              >
                                Deficiency Report
                              </SMButton>
                            )}
                          </>
                        )}
                      </>
                    )}
                    {isChecklistReview &&
                      checklist?.status === 'Meets Standards' &&
                      checklist?.processedOn === null &&
                      canProcessPO && (
                        <LoadingButton
                          sx={{
                            marginBottom: {
                              xs: '1rem',
                              md: 0
                            },
                            marginRight: {
                              xs: 0,
                              md: '1rem'
                            }
                          }}
                          color="primary"
                          variant="contained"
                          loading={isProcessingPO}
                          disabled={isProcessingPO}
                          loadingPosition="start"
                          startIcon={<PublishedWithChanges />}
                          onClick={handleProcessingPO}
                        >
                          Process PO
                        </LoadingButton>
                      )}
                    {isChecklistReview && checklist.sagePoExists && (
                      <LoadingButton
                        color="secondary"
                        variant="contained"
                        loading={isRequesting}
                        disabled={isRequesting}
                        loadingPosition="start"
                        startIcon={<CheckCircleOutline />}
                        onClick={handleRequestRecheck}
                      >
                        Request Recheck
                      </LoadingButton>
                    )}
                    {!isChecklistReview && !isModalPreview && (
                      <>
                        {isMobile ? (
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                              gap: 0.5
                            }}
                          >
                            <ButtonGroup sx={{ width: '100%' }}>
                              <SMButton
                                id="save"
                                color="secondary"
                                disabled={!isDirty || isSaving}
                                variant="contained"
                                startIcon={<Save />}
                                onClick={handleSaving}
                                fullWidth
                              >
                                Save
                              </SMButton>
                              <LoadingButton
                                variant="contained"
                                disabled={
                                  isAddingToPunchlist ||
                                  checklistLoading ||
                                  (hasCreatedPunchlist && !isDirty)
                                }
                                loadingPosition="start"
                                loading={isAddingToPunchlist}
                                onClick={handleAddToPunchlist}
                                fullWidth
                              >
                                {checklistLoading
                                  ? 'Loading...'
                                  : hasCreatedPunchlist
                                  ? 'Save and Update Punchlist'
                                  : 'Save and Add to Punchlist'}
                              </LoadingButton>
                            </ButtonGroup>
                            <SMButton
                              id="complete"
                              color="primary"
                              disabled={isChecklistDisabled}
                              variant="contained"
                              startIcon={<CheckBox />}
                              onClick={handleCompleting}
                              fullWidth
                            >
                              Complete
                            </SMButton>
                          </Box>
                        ) : (
                          <ButtonGroup>
                            <SMButton
                              id="save"
                              color="secondary"
                              disabled={!isDirty || isSaving}
                              variant="contained"
                              startIcon={<Save />}
                              onClick={handleSaving}
                            >
                              Save Items
                            </SMButton>
                            <LoadingButton
                              variant="contained"
                              disabled={
                                isAddingToPunchlist ||
                                checklistLoading ||
                                (hasCreatedPunchlist && !isDirty)
                              }
                              loadingPosition="start"
                              loading={isAddingToPunchlist}
                              onClick={handleAddToPunchlist}
                            >
                              {checklistLoading
                                ? 'Loading...'
                                : hasCreatedPunchlist
                                ? 'Save and Update Punchlist'
                                : 'Save and Add to Punchlist'}
                            </LoadingButton>
                            <SMButton
                              id="save"
                              color="primary"
                              disabled={isChecklistDisabled}
                              variant="contained"
                              startIcon={<CheckBox />}
                              onClick={handleCompleting}
                            >
                              CheckList Complete
                            </SMButton>
                          </ButtonGroup>
                        )}
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
};
